import { useRef, useEffect } from "react";

/**
 * ThermalBackground
 * A kinetic canvas of flowing "thermal energy" currents — evoking heat moving
 * through insulated pipe. Royal-blue (#1a3fd1) glowing streams drifting over a
 * near-black void. Performant single-canvas render with requestAnimationFrame.
 */
const ThermalBackground = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // Thermal streams — each is a drifting sine current
    const STREAMS = 7;
    const streams = Array.from({ length: STREAMS }).map((_, i) => ({
      baseY: 0.12 + (i / (STREAMS - 1)) * 0.76,
      amp: 30 + Math.random() * 70,
      freq: 0.6 + Math.random() * 1.4,
      speed: 0.0004 + Math.random() * 0.0006,
      phase: Math.random() * Math.PI * 2,
      hueShift: Math.random(),
      lineWidth: 1 + Math.random() * 1.6,
    }));

    // Stream colours come from the active theme (theme-login.css sets
    // --login-rgb-1 / --login-rgb-2 on <html>); royal blue is the Default.
    let rgb1 = "26, 63, 209";
    let rgb2 = "79, 111, 232";
    let lastPalette = 0;
    const readPalette = (now) => {
      if (now - lastPalette < 500) return;
      lastPalette = now;
      const cs = getComputedStyle(document.documentElement);
      rgb1 = cs.getPropertyValue("--login-rgb-1").trim() || "26, 63, 209";
      rgb2 = cs.getPropertyValue("--login-rgb-2").trim() || "79, 111, 232";
    };
    const colorFor = (t) => (t < 0.5 ? rgb1 : rgb2);

    let start = performance.now();

    const draw = (now) => {
      const elapsed = now - start;
      readPalette(now);
      ctx.clearRect(0, 0, width, height);

      // subtle central radial glow
      const grad = ctx.createRadialGradient(
        width * 0.5,
        height * 0.5,
        0,
        width * 0.5,
        height * 0.5,
        Math.max(width, height) * 0.7
      );
      grad.addColorStop(0, `rgba(${rgb1}, 0.10)`);
      grad.addColorStop(1, "rgba(5, 5, 5, 0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      streams.forEach((s, idx) => {
        const y0 = s.baseY * height;
        ctx.beginPath();
        const steps = 60;
        for (let i = 0; i <= steps; i++) {
          const x = (i / steps) * width;
          const progress = i / steps;
          const wave =
            Math.sin(progress * Math.PI * 2 * s.freq + elapsed * s.speed + s.phase) *
            s.amp;
          const wave2 =
            Math.cos(progress * Math.PI * 3 * s.freq - elapsed * s.speed * 0.7) *
            (s.amp * 0.35);
          const y = y0 + wave + wave2;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        const rgb = colorFor(s.hueShift);
        ctx.strokeStyle = `rgba(${rgb}, ${0.35 + (idx % 2) * 0.15})`;
        ctx.lineWidth = s.lineWidth;
        ctx.shadowColor = `rgba(${rgb}, 0.9)`;
        ctx.shadowBlur = 14;
        ctx.stroke();
        ctx.shadowBlur = 0;
      });

      // drifting energy nodes
      streams.forEach((s, idx) => {
        const t = ((elapsed * s.speed * 90 + idx * 40) % (width + 100)) - 50;
        const progress = t / width;
        const wave =
          Math.sin(progress * Math.PI * 2 * s.freq + elapsed * s.speed + s.phase) *
          s.amp;
        const y = s.baseY * height + wave;
        const rgb = colorFor(s.hueShift);
        ctx.beginPath();
        ctx.arc(t, y, 2.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb}, 0.9)`;
        ctx.shadowColor = `rgba(${rgb}, 1)`;
        ctx.shadowBlur = 18;
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      if (!prefersReduced) rafRef.current = requestAnimationFrame(draw);
    };

    if (prefersReduced) {
      draw(performance.now());
    } else {
      rafRef.current = requestAnimationFrame(draw);
    }

    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="login-thermal"
      data-testid="thermal-background"
      className="fixed inset-0 h-full w-full"
    />
  );
};

export default ThermalBackground;
